import { useState, useEffect, memo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UserActions } from 'BMapsCmds';
import { hasLogin } from 'BMapsSrc/server/session_request';
import { Link } from '@mui/material';
import { SidePanelContainer } from './info_display/SidePanel';
import { ErrorBoundary } from './PageWrapper';
import { ChatBot } from './ChatBot';
import ExternalLink from './common/ExternalLink';
import { MainTour } from './Tour';
import { TheManual } from './manual/TheManual';
import DialogContentSection from './common/DialogContentSection';

// 2026-06-01: HelpPanel wrapped in React.memo so it doesn't re-render
// during sidepanel resize drags. The right-sidepanel column updates
// `paneState` on every mousemove; without memo, the entire HelpPanel
// subtree (including the 2000-line ChatBot) re-renders per mousemove,
// causing visible drag lag. HelpPanel's props are stable during a
// drag (open/handleClose/etc. unchanged), so React.memo's default
// shallow-equality compare bails out and the subtree is skipped.
export const HelpPanel = memo(HelpPanelImpl);

function HelpPanelImpl({
    handleClose, open, longLived, ChatBotProps, isStacked,
}) {
    const [loggedIn, setLoggedIn] = useState(false);
    useEffect(() => {
        hasLogin().then((newLoggedIn) => setLoggedIn(newLoggedIn));
    }, []);
    return (
        <SidePanelContainer
            open={open}
            handleClose={handleClose}
            longLived={longLived}
            isStacked={isStacked}
            title="Help / Support"
            paneId="help"
            containerStyle={{
                background: '#DCF0E6',
                // set z index to 0 to override default
                // right side panel needs to be under the bfm_viewer so that tooltips are
                // displayed on top of it. this is not true for left side panel
                // which is on top of the bfm_viewer
                zIndex: 0,
            }}
        >
            <Typography component="div">
                <DialogContentSection
                    titleVariant="subtitle1"
                    collapsible
                    disablePaddingTop
                    title="Help Information"
                    TitleProps={{ style: { padding: '0 .5rem' } }}
                >
                    <HelpSection title="In-App Help:" style={{ paddingTop: 0 }}>
                        <HelpButton onClick={() => MainTour.start()}>Launch Tour</HelpButton>
                        <HelpButton onClick={() => TheManual.ShowPage(TheManual.Pages.Main)}>
                            Open Manual
                        </HelpButton>
                        <HelpButton onClick={() => TheManual.ShowPage(TheManual.Pages.Cheatsheet)}>
                            Cheatsheet
                        </HelpButton>
                    </HelpSection>
                    <HelpSection title="Documentation:">
                        <HelpLink link="https://www.youtube.com/channel/UCoFsnMxpCs1NnTB-9JAb1fw?&ab_channel=ConiferPoint">Tutorial Videos</HelpLink>
                        <HelpLink link="https://www.boltzmannmaps.com/faqs">Frequently Asked Questions</HelpLink>
                        <HelpLink link="https://www.boltzmannmaps.com/documentation">Documentation Pages</HelpLink>
                    </HelpSection>
                    <HelpSection title="Contact Us:">
                        <HelpLink link="mailto:support@coniferpoint.com">support@coniferpoint.com</HelpLink>
                    </HelpSection>
                    { !!loggedIn && (
                        <HelpSection title="Connect:">
                            <HelpLink link="https://join.slack.com/t/bmaps-community/shared_invite/zt-2kuban85x-hbntjszQ7lUfTn2pj0j0Rg">
                                Join our Slack community
                            </HelpLink>
                        </HelpSection>
                    )}
                </DialogContentSection>
            </Typography>
            <ErrorBoundary surfaceName="Gibbs chat">
                <ChatBot
                    loggedIn={loggedIn}
                    {...ChatBotProps}
                />
            </ErrorBoundary>
        </SidePanelContainer>
    );
}

function HelpLink({ link, children }) {
    return (
        <Box>
            <ExternalLink
                link={link}
                style={{ color: 'var(--marketing-blue)' }}
                skipIcon
            >
                {children}
            </ExternalLink>
        </Box>
    );
}

function HelpSection({ title, style, children }) {
    const styleToUse = { paddingTop: '0.5rem', ...style };
    return (
        <Box style={styleToUse}>
            {title}
            {children}
        </Box>
    );
}

const HelpButton = ({ onClick, children }) => (
    <Box>
        <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
            component="button"
            onClick={onClick}
            sx={{ color: 'var(--marketing-blue)' }}
        >
            {children}
        </Link>
    </Box>
);

/**
 * @param {{ ChatBotProps?: { initalMessages: [string]} }} props
 */
export function openHelpMenu(props) {
    UserActions.OpenSidePanel('right', { pageId: 'help', props });
}

export function toggleHelpMenu() {
    UserActions.OpenSidePanel('right', { pageId: 'help', toggle: true });
}
